/* eslint-disable react-refresh/only-export-components */
import { MessageOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  useEnabledItems,
  useMenuItems,
  useMenuModifiers,
} from "../../../../hooks/useMenu";
import type { DisplayRow } from "./billSummaryUtils";
import { LunchTag } from "../../lunch/LunchTag";

// Статус на реда за визуализация. Сервирането е ОТДЕЛНА ос (`served`) и бие
// кухненския статус: сервиран ред е зелен независимо докъде е стигнала
// кухнята. Иначе: "0" незапочнато (неутрално), "1" в процес (светло
// оранжево), "2" сготвено (тъмно оранжево), "3" издадено от шпикер (синьо).
// Останалото (сторно/грешка) — danger токените. Индикаторът е лява лента +
// кръгла иконка (без текстов pill).
export type RowStatusKey =
  | "served"
  | "issued"
  | "ready"
  | "progress"
  | "none"
  | "danger";

const rowStatusKey = (status: string, served: boolean): RowStatusKey => {
  if (served) return "served";
  switch (status) {
    case "3":
      return "issued";
    case "2":
      return "ready";
    case "1":
      return "progress";
    case "0":
      return "none";
    default:
      return "danger";
  }
};

const stripClassByKey: Record<RowStatusKey, string> = {
  served: "bg-pos-served",
  issued: "bg-pos-issued",
  ready: "bg-pos-ready",
  progress: "bg-pos-progress",
  none: "bg-transparent",
  danger: "bg-pos-danger-ink",
};

export const iconInkByKey: Record<RowStatusKey, string> = {
  served: "text-pos-served",
  issued: "text-pos-issued",
  ready: "text-pos-ready",
  progress: "text-pos-progress",
  none: "text-pos-none",
  danger: "text-pos-danger-ink",
};

// Кръглата статус-иконка: празен кръг = незапочнато, полузапълнен = в
// процес, плътен с „!" = сготвено, плътен със стрелка = издадено, плътен с
// отметка = сервирано, плътен с „×" = сторно/грешка.
export const StatusIcon = ({ statusKey }: { statusKey: RowStatusKey }) => (
  <svg viewBox="0 0 20 20" className="w-[19px] h-[19px]" aria-hidden>
    {statusKey === "none" && (
      <circle
        cx="10"
        cy="10"
        r="7.75"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
    )}
    {statusKey === "progress" && (
      <>
        <circle
          cx="10"
          cy="10"
          r="7.75"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        {/* дясната половина е запълнена — „наполовина готово" */}
        <path d="M10 2.25 A7.75 7.75 0 0 1 10 17.75 Z" fill="currentColor" />
      </>
    )}
    {statusKey !== "none" && statusKey !== "progress" && (
      <circle cx="10" cy="10" r="8.75" fill="currentColor" />
    )}
    {statusKey === "ready" && (
      <>
        <rect x="9" y="4.75" width="2" height="6.5" rx="1" className="fill-white" />
        <circle cx="10" cy="14.25" r="1.25" className="fill-white" />
      </>
    )}
    {statusKey === "issued" && (
      <path
        d="M10 4.6 L14.2 9.3 H11.4 V15.2 H8.6 V9.3 H5.8 Z"
        className="fill-white"
      />
    )}
    {statusKey === "served" && (
      <path
        d="M6.2 10.4 L8.9 13 L13.9 7.4"
        fill="none"
        className="stroke-white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
    {statusKey === "danger" && (
      <path
        d="M7 7 L13 13 M13 7 L7 13"
        fill="none"
        className="stroke-white"
        strokeWidth="2"
        strokeLinecap="round"
      />
    )}
  </svg>
);

// Знаков процент на ред: положителен = отстъпка („-10.00%"), отрицателен =
// надбавка („+10.00%"). BE-то праща percent ту като string, ту като number —
// нормализираме през String().
const fmtLinePercent = (p: string | number): string => {
  const s = String(p);
  return s.startsWith("-") ? `+${s.slice(1)}%` : `-${s}%`;
};

const formatQuantity = (quantity: number) => {
  const rounded = Math.round(quantity * 10) / 10;
  return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(1);
};

interface OrderItemRowProps {
  /** Ref към реда — за скрола до избрания ред (стрелките ↑ ↓). */
  rowRef?: (element: HTMLDivElement | null) => void;
  row: DisplayRow;
  isCompactLayout: boolean;
  isSelected: boolean;
  /** Липсва в обобщения изглед — там редът не е избираем. */
  onClick?: () => void;
  /** Двоен клик — бърза повторна поръчка на продукта. */
  onDoubleClick?: () => void;
  /** Сервира/отсервира реда (toggle) — подава се само в необобщения изглед. */
  onServe?: (e: React.MouseEvent) => void;
}

export const OrderItemRow = ({
  rowRef,
  row,
  isCompactLayout,
  isSelected,
  onClick,
  onDoubleClick,
  onServe,
}: OrderItemRowProps) => {
  const { t, i18n } = useTranslation();
  const allItems = useEnabledItems();
  const allModifiers = useMenuModifiers();
  const allMenuItems = useMenuItems();

  const { item, quantity } = row;
  // ПЛУ кодът от менюто — до името, както в каталога; без код не се показва
  // нищо. Търси се и сред изключените продукти: вече поръчаният ред си остава.
  const pluCode =
    allMenuItems.find((menuItem) => menuItem.id === item.item_id)?.pluCode ??
    null;
  const statusKey = rowStatusKey(item.status, item.served);

  const getItemName = (itemNames: Record<string, string>) =>
    itemNames[i18n.language] ||
    itemNames.bg ||
    Object.values(itemNames)[0] ||
    t("common.product");

  const getModifierName = (modifierId: string) => {
    const modifier = allModifiers.find((m) => m.id === parseInt(modifierId));
    if (!modifier) return modifierId;
    const translation = modifier.translations.find(
      (tr) => tr.languageCode === i18n.language,
    );
    return translation?.name || modifier.slug;
  };

  const getAddonItemName = (addonItemId: number) => {
    const addonItem = allItems.find((i) => i.id === addonItemId);
    if (!addonItem) return `#${addonItemId}`;
    const itemLang = addonItem.itemLangs.find(
      (l) => l.languageCode === i18n.language,
    );
    return itemLang?.name || addonItem.slug;
  };

  const price = parseFloat(item.price);
  // знаков процент: >0 отстъпка, <0 надбавка
  const hasAdjustment = parseFloat(item.percent) !== 0;
  // Собствената цена („Промяна на цена") ползва същото поле `percent`, но НЕ
  // е отстъпка — показваме само задрасканата стара сума и новата, БЕЗ бадж.
  const isCustomPrice = item.custom_price;
  const hasDiscount = hasAdjustment && !isCustomPrice;
  // Всяка корекция (ръчна цена ИЛИ отстъпка/надбавка) се обявява на
  // ЕДИНИЧНАТА цена: „qty × ~~стара~~ действаща", дори при количество 1.
  // Задраскан общ сбор няма — тоталът (с добавките) остава чист, а при
  // отстъпка процентът стои като бадж между цената и тотала.
  const unitPrice = hasAdjustment
    ? Math.round(price * (100 - parseFloat(item.percent))) / 100
    : price;
  const showUnitPrice = quantity !== 1 || hasAdjustment;

  // Крайната стойност се чете от самите добавки: отстъпката върху продукт ги
  // обхваща (BE ги преизчислява), а промяната на цената — не (тя пипа само
  // продукта). Затова НЕ смятаме от `discountFactor`, а сумираме total_sum.
  const additionsAfter = row.additions.reduce(
    (sum, addition) => sum + addition.totalSum,
    0,
  );
  const total = row.totalSum + additionsAfter;

  // Модификаторите пристигат смесени с коментарите към продукта (FE изпраща
  // коментара като modifier_id-текст) — разделяме ги: истински модификатор =
  // намерен по числово id в менюто.
  const realModifiers: string[] = [];
  const itemComments: string[] = [];
  item.modifiers.forEach((m) => {
    const found = allModifiers.find((x) => x.id === parseInt(m.modifier_id));
    if (found) realModifiers.push(getModifierName(m.modifier_id));
    else itemComments.push(m.modifier_id);
  });

  // Chips: само добавките (модификаторите са на собствен ред)
  const chipsRow = row.additions.length > 0 && (
    <div className="flex flex-wrap gap-1">
      {row.additions.map((addition, idx) => {
        const a = addition.representative;
        const addonPrice = parseFloat(a.price);
        const addonName = getAddonItemName(a.addition_item_id);
        // Коригирана цена на ДОБАВКА — и ръчна цена, и отстъпка/надбавка се
        // изразяват в percent на реда ѝ: задраскваме цената от менюто и
        // показваме действащата — както прави и продуктовият ред.
        const addonPercent = parseFloat(a.percent);
        const addonPriceAdjusted = addonPercent !== 0;
        const addonNewPrice =
          Math.round(addonPrice * (100 - addonPercent)) / 100;
        return (
          <span
            key={idx}
            title={addonName}
            className={`min-w-0 inline-flex items-center gap-1 bg-pos-addon-bg border border-pos-addon-brd text-pos-addon-ink rounded-[7px] px-1.5 py-0.5 ${isCompactLayout ? "text-[11px]" : "text-[13px] leading-[16.5px]"}`}
          >
            <span className="text-pos-disc-ink">＋</span>
            <span className="truncate">{addonName}</span>
            <span className="font-pos-mono whitespace-nowrap">
              {formatQuantity(addition.quantity)} ×{" "}
              {addonPriceAdjusted ? (
                <>
                  <span className="line-through opacity-70">
                    {addonPrice.toFixed(2)}
                  </span>{" "}
                  {addonNewPrice.toFixed(2)}
                </>
              ) : (
                addonPrice.toFixed(2)
              )}
            </span>
          </span>
        );
      })}
    </div>
  );

  // Модификаторите — на собствен ред, червени като коментара
  // (същият стил като в „Преглед на поръчката" при добавянето)
  const modifiersRow = realModifiers.length > 0 && (
    <div className={`min-w-0 font-semibold italic text-pos-danger-ink break-words ${isCompactLayout ? "text-[12.5px]" : "text-[14.5px] leading-[19px]"}`}>
      {realModifiers.join(" · ")}
    </div>
  );

  // Кръглата статус-иконка като бутон: на десктоп е собствена колонка след
  // лентата (центрирана по реда), на телефон седи вътре в реда с името.
  const statusIconButton = (placementClasses: string) => (
    <button
      type="button"
      title={item.served ? t("ordering.servedStatus") : item.status_name}
      onClick={onServe}
      onDoubleClick={(e) => e.stopPropagation()}
      className={`shrink-0 border-0 bg-transparent grid place-items-center ${
        iconInkByKey[statusKey]
      } ${onServe ? "cursor-pointer" : "cursor-default"} ${placementClasses}`}
    >
      <StatusIcon statusKey={statusKey} />
    </button>
  );

  // „| 123" след името — стилът на кода в каталога (ItemCard); shrink-0, за
  // да не се реже, когато дългото име се съкращава с „…".
  const pluCodeLabel = (extraClasses: string) =>
    pluCode != null && (
      <span
        className={`shrink-0 whitespace-pre font-pos-mono font-medium text-pos-muted ${extraClasses}`}
      >
        | {pluCode}
      </span>
    );

  // Коментар към продукта — на собствен ред, червен и четим
  const commentRow = itemComments.length > 0 && (
    <div className={`flex items-start gap-1.5 min-w-0 font-semibold italic text-pos-danger-ink ${isCompactLayout ? "text-[13px]" : "text-[15px] leading-[19.5px]"}`}>
      <MessageOutlined className="text-xs shrink-0 mt-1" />
      <span className="min-w-0 break-words">{itemComments.join(" · ")}</span>
    </div>
  );

  return (
    <div
      ref={rowRef}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      className={`flex rounded-[10px] overflow-hidden border ${
        onClick ? "cursor-pointer" : "cursor-default"
      } ${
        isSelected
          ? "bg-pos-marked border-pos-accent"
          : "bg-pos-card border-pos-line3"
      }`}
    >
      {/* Лява статус-лента + кръгла иконка — двата индикатора за статус.
          Кликът върху тях сервира реда, а върху сервиран ред връща
          кухненския статус (toggle) */}
      <button
        type="button"
        title={item.served ? t("ordering.servedStatus") : item.status_name}
        onClick={onServe}
        // Двоен клик на лентата не тригерва повторна поръчка
        onDoubleClick={(e) => e.stopPropagation()}
        className={`self-stretch shrink-0 border-0 p-0 ${
          isCompactLayout && statusKey === "none" ? "w-0" : "w-1.5"
        } ${
          stripClassByKey[statusKey]
        } ${onServe ? "cursor-pointer" : "cursor-default"}`}
      />
      {!isCompactLayout && statusIconButton("self-center pl-2 pr-0 py-1")}

      <div
        className={`${
          isCompactLayout ? "pl-2 pr-2" : "pl-2.5 pr-3"
        } py-[7px] relative flex-1 min-w-0 flex flex-col gap-1`}
      >
        {isCompactLayout ? (
          /* ТЕСЕН ЕКРАН: stacked — иконка+име+qty×цена, chips, коментар, отстъпка+тотал */
          <>
            <div className="flex items-center min-w-0 pr-7">
              <span
                title={getItemName(item.item_names)}
                className="min-w-0 truncate text-sm font-semibold text-pos-ink"
              >
                {getItemName(item.item_names)}
              </span>
              {pluCodeLabel("ml-1 text-xs")}
              {/* „Обедно" вдясно — подравнено по редовете (не залепено за името) */}
              {item.lunch_menu_id != null && (
                <span className="ml-auto shrink-0 pl-2">
                  <LunchTag />
                </span>
              )}
            </div>
            {/* Иконката стои на реда с името (горе вдясно, 8px от ръба —
                колкото е името отляво), а НЕВИДИМИЯТ ѝ бутон покрива десните
                33% от кутийката по цялата височина — клик където и да е там
                сервира/отсервира (!flex бие grid-а от общите класове) */}
            {statusIconButton(
              "absolute inset-y-0 right-0 w-1/3 !flex items-start justify-end pr-2 pt-[7px]",
            )}

            {chipsRow}
            {modifiersRow}
            {commentRow}

            {/* Ред на сметката: qty × ~~стара~~ действаща цена · % → тотал */}
            <div className="flex items-baseline justify-end gap-2">
              {showUnitPrice && (
                <span className="font-pos-mono text-xs text-pos-muted whitespace-nowrap">
                  {formatQuantity(quantity)} ×{" "}
                  {hasAdjustment && (
                    <>
                      <span className="line-through opacity-70">
                        {price.toFixed(2)}
                      </span>{" "}
                    </>
                  )}
                  {unitPrice.toFixed(2)}
                </span>
              )}
              {hasDiscount && (
                <span className="text-[11px] font-semibold text-pos-disc-ink">
                  {fmtLinePercent(item.percent)}
                </span>
              )}
              <span className="font-pos-mono text-sm font-bold text-pos-ink min-w-[46px] text-right">
                {total.toFixed(2)}
              </span>
            </div>
          </>
        ) : (
          /* ДЕСКТОП: ред 1 — име · (отстъпка) · qty×цена · тотал; ред 2 — chips; ред 3 — коментар */
          <>
            <div className="flex items-center gap-2 min-w-0">
              <span
                title={getItemName(item.item_names)}
                className="min-w-0 truncate text-base leading-[21px] font-semibold text-pos-ink"
              >
                {getItemName(item.item_names)}
              </span>
              {pluCodeLabel("text-sm leading-[18px]")}
              {/* Ред на сметката: („Обедно") qty × ~~стара~~ действаща цена · % → тотал;
                  етикетът е в дясната група, за да стои в колона по редовете */}
              <span className="ml-auto shrink-0 inline-flex items-center gap-2">
                {item.lunch_menu_id != null && <LunchTag className="mr-2" />}
                {showUnitPrice && (
                  <span className="font-pos-mono text-sm leading-[18px] text-pos-muted whitespace-nowrap">
                    {formatQuantity(quantity)} ×{" "}
                    {hasAdjustment && (
                      <>
                        <span className="line-through opacity-70">
                          {price.toFixed(2)}
                        </span>{" "}
                      </>
                    )}
                    {unitPrice.toFixed(2)}
                  </span>
                )}
                {hasDiscount && (
                  <span className="text-[13px] leading-[16.5px] font-semibold text-pos-disc-ink">
                    {fmtLinePercent(item.percent)}
                  </span>
                )}
                <span className="font-pos-mono text-base leading-[21px] font-bold text-pos-ink min-w-[54px] text-right">
                  {total.toFixed(2)}
                </span>
              </span>
            </div>

            {chipsRow}
            {modifiersRow}
            {commentRow}
          </>
        )}
      </div>
    </div>
  );
};
